import { useState, useEffect, useCallback } from 'react'
import TranslatableText from '../components/TranslatableText.jsx'
import StaffChat from '../components/StaffChat.jsx'
import { useChatUnread, registerStaffDevice } from '../services/staffChat.js'
import { useViewParams } from '../services/viewHistory.js'
import {
  readPastorToken, readPastorProfile, pastorLogin, pastorLogout, clearPastorSession,
} from '../services/pastorAuth.js'

const API = import.meta.env.VITE_API_URL || ''

const VIEWS = [
  { value: 'open', label: 'Open' },
  { value: 'answered', label: 'Answered' },
]

function formatDate(str) {
  return new Date(str).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
}

// Dashboard der verifizierten Seelsorger: Bitten um Seelsorge beantworten
export default function PastorPage() {
  const [profile, setProfile] = useState(readPastorProfile)
  const [view, setView] = useState('open')
  const [items, setItems] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  // Unteransichten in der URL: ?screen=chat|password, &c=<Unterhaltung>, &new=1
  const nav = useViewParams()
  const screen = nav.params.get('screen')
  const activeChat = parseInt(nav.params.get('c')) || null
  const leaveChat = useCallback(() => nav.back({ screen: null, c: null, new: null }), [nav])
  const showPassword = screen === 'password'
  const { unread, refresh: refreshUnread } = useChatUnread(readPastorToken, !!profile)

  useEffect(() => { if (profile) registerStaffDevice() }, [profile])

  const api = useCallback(async (path, options = {}) => {
    const res = await fetch(`${API}/api/pastor${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${readPastorToken()}`,
        ...options.headers,
      },
    })
    const data = await res.json().catch(() => ({}))
    if (res.status === 401) {
      // Sitzung abgelaufen, Konto gesperrt oder Passwort geändert
      clearPastorSession()
      setProfile(null)
    }
    if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`)
    return data
  }, [])

  const load = useCallback(async () => {
    if (!profile) return
    setLoading(true)
    setError(null)
    try {
      const [s, list] = await Promise.all([api('/stats'), api(`/requests?view=${view}`)])
      setStats(s)
      setItems(list)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [api, profile, view])

  useEffect(() => { load() }, [load])

  const act = useCallback(async (path) => {
    try {
      await api(path, { method: 'POST', body: '{}' })
      await load()
    } catch (err) {
      setError(err.message)
    }
  }, [api, load])

  async function logout() {
    await pastorLogout()
    setProfile(null)
  }

  if (!profile) return <PastorLogin onLogin={p => { setError(null); setProfile(p) }} />

  if (screen === 'chat') {
    return (
      <div className="admin admin-chat">
        <header className="admin-header">
          <button className="admin-back" onClick={leaveChat}>← Pastoral care</button>
          <h1>Chat</h1>
        </header>
        <StaffChat
          getToken={readPastorToken}
          activeId={activeChat}
          showPicker={nav.params.get('new') === '1'}
          onOpen={id => (activeChat ? nav.replace({ c: id, new: null }) : nav.push({ c: id, new: null }))}
          onPicker={() => nav.push({ new: 1 })}
          onStart={id => nav.replace({ new: null, c: id })}
          onBack={() => nav.back({ c: null, new: null })}
          onActivity={refreshUnread}
        />
      </div>
    )
  }

  return (
    <div className="admin">
      <header className="admin-header">
        <a href="/" className="admin-back">← Reverend</a>
        <h1>Pastoral care</h1>
        <span className="pastor-whoami">✝ {profile.display_name}</span>
        <button className="btn btn-ghost chat-open-btn" onClick={() => nav.push({ screen: 'chat' })} aria-label={`Chat${unread ? `, ${unread} unread` : ''}`}>
          💬 Chat
          {unread > 0 && <span className="chat-unread">{unread > 99 ? '99+' : unread}</span>}
        </button>
        <button className="btn btn-ghost" onClick={() => (showPassword ? nav.back({ screen: null }) : nav.push({ screen: 'password' }))} aria-expanded={showPassword}>
          Change password
        </button>
        <button className="btn btn-ghost" onClick={logout}>Log out</button>
      </header>

      {showPassword && <ChangePasswordForm api={api} onDone={() => nav.back({ screen: null })} />}

      {stats && (
        <div className="admin-stats">
          <Stat label="Waiting for a reply" value={stats.open} warn={stats.open > 0} />
          <Stat label="Answered" value={stats.answered} />
          <Stat label="Answered by you" value={stats.answered_by_me} />
        </div>
      )}

      <div className="admin-toolbar">
        <div className="sort-tabs" role="tablist">
          {VIEWS.map(v => (
            <button
              key={v.value}
              role="tab"
              aria-selected={view === v.value}
              className={`sort-tab ${view === v.value ? 'active' : ''}`}
              onClick={() => setView(v.value)}
            >
              {v.label}
            </button>
          ))}
        </div>
        <button className="btn btn-ghost" onClick={load} disabled={loading}>Refresh</button>
      </div>

      {error && <div className="feed-state feed-error" role="alert">{error}</div>}
      {loading && <div className="feed-state"><div className="spinner" aria-label="Loading…" /></div>}

      {!loading && items.length === 0 && (
        <div className="feed-state feed-empty">
          {view === 'open' ? 'No one is waiting right now. 🙏' : 'Nothing answered yet.'}
        </div>
      )}

      <div className="admin-list">
        {items.map(r => (
          <PrayerRequest key={r.id} request={r} api={api} act={act} onReplied={load} />
        ))}
      </div>
    </div>
  )
}

function Stat({ label, value, warn }) {
  return (
    <div className={`admin-stat ${warn ? 'warn' : ''}`}>
      <span className="admin-stat-value">{value}</span>
      <span className="admin-stat-label">{label}</span>
    </div>
  )
}

function PrayerRequest({ request: r, api, act, onReplied }) {
  const [comments, setComments] = useState(null)
  const [open, setOpen] = useState(false)
  const [reply, setReply] = useState('')
  const [replying, setReplying] = useState(false)
  const [replyError, setReplyError] = useState(null)

  async function toggleComments() {
    if (!open && comments === null) {
      try { setComments(await api(`/requests/${r.id}/comments`)) } catch { setComments([]) }
    }
    setOpen(o => !o)
  }

  // Antwort erscheint öffentlich als Kommentar mit Namen des Seelsorgers
  async function sendReply(e) {
    e.preventDefault()
    const text = reply.trim()
    if (!text) return
    setReplying(true)
    setReplyError(null)
    try {
      const res = await fetch(`${API}/api/confessions/${r.id}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${readPastorToken()}` },
        body: JSON.stringify({ text }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Error posting reply.')
      setReply('')
      setComments(null)
      setOpen(false)
      await onReplied()
    } catch (err) {
      setReplyError(err.message)
    } finally {
      setReplying(false)
    }
  }

  return (
    <div className="admin-item">
      <div className="admin-item-meta">
        <span>#{r.id}</span>
        <span>{formatDate(r.created_at)}</span>
        {r.pastor_answered_at
          ? <span className="admin-tag ok">✝ answered{r.answered_by ? ` by ${r.answered_by}` : ''}</span>
          : <span className="admin-tag pastor">waiting</span>}
      </div>

      <TranslatableText className="admin-item-text" text={r.text} />

      <div className="admin-actions">
        {r.pastor_answered_at
          ? <button className="action-btn" onClick={() => act(`/requests/${r.id}/reopen`)}>Reopen</button>
          : <button className="action-btn approve" onClick={() => act(`/requests/${r.id}/done`)}>✓ Mark done</button>}
        <button className="action-btn" onClick={toggleComments} aria-expanded={open}>
          💬 {r.comment_count}
        </button>
      </div>

      {open && comments && (
        <ul className="admin-comments">
          {comments.length === 0 && <li className="comment-empty">No comments.</li>}
          {comments.map(cm => (
            <li key={cm.id}>
              <span className="admin-comment-author">
                {cm.pastor_name ? `✝ ${cm.pastor_name}` : cm.is_admin ? '✝ Reverend' : (cm.author_tag || '—')}
                {cm.is_op && ' (OP)'}
              </span>
              <span className="admin-comment-text">{cm.text}</span>
            </li>
          ))}
        </ul>
      )}

      <form className="admin-reply" onSubmit={sendReply}>
        <textarea
          className="comment-input"
          rows={3}
          maxLength={200}
          placeholder="Write your reply…"
          value={reply}
          onChange={e => setReply(e.target.value)}
          disabled={replying}
        />
        <button className="btn btn-primary" type="submit" disabled={replying || !reply.trim()}>
          {replying ? 'Sending…' : 'Reply'}
        </button>
        {replyError && <p className="comment-error">{replyError}</p>}
      </form>
    </div>
  )
}

// Eigenes Passwort ändern – andere Geräte werden dabei ausgeloggt
function ChangePasswordForm({ api, onDone }) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [repeat, setRepeat] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  async function submit(e) {
    e.preventDefault()
    setError(null)
    if (next.length < 10) return setError('New password must be at least 10 characters.')
    if (next !== repeat) return setError('The new passwords do not match.')
    setBusy(true)
    try {
      await api('/password', {
        method: 'POST',
        body: JSON.stringify({ current_password: current, new_password: next }),
      })
      setSuccess(true)
      setCurrent(''); setNext(''); setRepeat('')
      setTimeout(onDone, 2500)
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="admin-item pastor-form" onSubmit={submit}>
      <strong>Change password</strong>
      <input className="comment-input" type="password" placeholder="Current password" autoComplete="current-password" value={current} onChange={e => setCurrent(e.target.value)} autoFocus />
      <input className="comment-input" type="password" placeholder="New password (min. 10 characters)" autoComplete="new-password" value={next} onChange={e => setNext(e.target.value)} />
      <input className="comment-input" type="password" placeholder="Repeat new password" autoComplete="new-password" value={repeat} onChange={e => setRepeat(e.target.value)} />
      <div className="pastor-form-actions">
        <button className="btn btn-ghost" type="button" onClick={onDone}>Cancel</button>
        <button className="btn btn-primary" type="submit" disabled={busy || !current || !next || !repeat}>
          {busy ? 'Saving…' : 'Save password'}
        </button>
      </div>
      {error && <p className="comment-error">{error}</p>}
      {success && <p className="pastor-success">Password changed. Other devices have been logged out.</p>}
    </form>
  )
}

function PastorLogin({ onLogin }) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  async function submit(e) {
    e.preventDefault()
    setLoading(true)
    setError(null)
    try {
      onLogin(await pastorLogin(username.trim(), password))
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="admin admin-login pastor-login">
      <a href="/" className="admin-back">← Reverend</a>
      <h1>✝ Pastor login</h1>
      <p className="pastor-login-note">For verified church representatives only.</p>
      <form onSubmit={submit}>
        <input
          className="comment-input"
          type="text"
          placeholder="Username"
          autoComplete="username"
          autoCapitalize="none"
          value={username}
          onChange={e => setUsername(e.target.value)}
          autoFocus
        />
        <input
          className="comment-input"
          type="password"
          placeholder="Password"
          autoComplete="current-password"
          value={password}
          onChange={e => setPassword(e.target.value)}
        />
        <button className="btn btn-primary" type="submit" disabled={loading || !username.trim() || !password}>
          {loading ? 'Logging in…' : 'Log in'}
        </button>
      </form>
      {error && <p className="comment-error">{error}</p>}
    </div>
  )
}
